'use client';

import React, { useState } from 'react';
import WeddingNav from '@/components/WeddingNav';

interface FAQItem {
  id: string;
  question: string;
  answer: string;
}

const faqs: FAQItem[] = [
  {
    id: 'faq-1',
    question: "I'm worried about how I'll get to the different venues?",
    answer:
      "Please don't worry, there's always a way. Just pop us a message and we'll help organise everything so you can move around with ease and enjoy the day.",
  },
  {
    id: 'faq-2',
    question: "I'm coming on my own and won't know anyone…",
    answer:
      "This is a very small, friendly and conviviale gathering. You'll always feel surrounded, and we'll make sure everyone feels part of it from the very beginning.",
  },
  {
    id: 'faq-3',
    question: "It seems like a long day, will there be somewhere to rest, or for children to go to bed early?",
    answer:
      "Yes, absolutely. We've set aside two bedrooms away from the celebrations for anyone who needs a moment to retreat, rest, or settle little ones.",
  },
  {
    id: 'faq-4',
    question: "I've seen the indigo theme, but I don't have anything that fits?",
    answer:
      "The theme is simply there to bring everything together in a relaxed, expressive way. Please don't feel the need to buy anything new, reuse, borrow, upcycle or create if you'd like. Indigo embraces every shade of blue imaginable, in whatever quantity feels right to you.",
  },
  {
    id: 'faq-5',
    question: "I have a formal navy suit… does it need to be that formal?",
    answer:
      "Not at all. It's a high-summer wedding with a relaxed but refined feel. Linen, softer tailoring and natural, flowing silhouettes are all very much in keeping with the atmosphere we'd love to create.",
  },
  {
    id: 'faq-6',
    question: "I'm not vegan, what will I eat?",
    answer:
      "There will be plenty of variety for everyone. Our aim is to create something generous and inclusive, so you'll be very well looked after.",
  },
  {
    id: 'faq-7',
    question: "I don't drink, will there be anything for me?",
    answer:
      "Absolutely, there will be an abundance of non-alcoholic options, including plenty of mocktails. If there's something you particularly enjoy or would recommend, do let us know, we'd love to include it.",
  },
  {
    id: 'faq-8',
    question: "What happens if it rains?",
    answer:
      "Then it becomes part of the atmosphere. There will always be somewhere warm, covered and welcoming. If you do bring extra layers for unpredictable weather, we have plenty of cloakroom space to accommodate everything.",
  },
  {
    id: 'faq-9',
    question: "I've looked at the regatta and there are things I'd love to see, but they overlap with the wedding…",
    answer:
      "Please don't worry, do take the time to soak up what Dartmouth has to offer. We chose the regatta weekend because we wanted to share its atmosphere and vibrancy with you, so feel free to embrace all elements of it.",
  },
  {
    id: 'faq-10',
    question: "I'm planning to stay and enjoy things after Sunday evening into Monday, but I didn't see anything about that?",
    answer:
      "We've kept this part a little under wraps so no one feels they're missing out. Let us know how long you're staying, and we can carry on the fun together.",
  },
  {
    id: 'faq-11',
    question: "I'd love to help or get involved, but I'm not a professional, what could I do?",
    answer:
      "Any help is so warmly welcomed. Whether it's baking, photography, crafts, cocktails or mocktails, or simply having an eye for detail, we'd love to hear from you.",
  },
  {
    id: 'faq-12',
    question: "I have a talent I'd love to share, or I'd like to give a speech, but I'm a bit shy…",
    answer:
      "Please do tell us. We'd genuinely love to hear about it and see how we can include it in a way that feels comfortable and special for you.",
  },
];

export default function FAQPage() {
  const [openId, setOpenId] = useState<string | null>(null);

  const toggle = (id: string) => {
    setOpenId((prev) => (prev === id ? null : id));
  };

  return (
    <main className="bg-buttermilk min-h-screen">
      <WeddingNav />

      {/* Hero */}
      <section className="relative w-full overflow-hidden">
        <img
          src="/assets/images/IMG_1113-1777579917181.jpg"
          alt="Wedding hero image"
          className="w-full h-auto block"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-deep-navy/5 to-deep-navy/65" />
        <div className="absolute bottom-0 left-0 right-0 z-10 px-8 md:px-16 xl:px-24 pb-10 lg:pb-16 max-w-screen-2xl mx-auto w-full">
          <p className="font-sans text-xs tracking-[0.3em] uppercase text-warm-sand/80 mb-4">
            George &amp; Boadecea · XXIX · VIII · MMXXVI
          </p>
          <h1 className="font-serif text-display-md text-off-white">
            Imagined Frequently Asked<br />Questions
          </h1>
        </div>
        <p
          className="absolute bottom-2 right-3 z-20 font-sans text-white/70 leading-none"
          style={{ fontSize: '9px', letterSpacing: '0.02em' }}
        >
          The ferry from Naoshima, Oct 24
        </p>
      </section>

      {/* FAQ list */}
      <section className="px-8 md:px-16 xl:px-24 pb-24 max-w-screen-2xl mx-auto">
        <div className="max-w-2xl">
          {faqs.map((faq, index) => {
            const isOpen = openId === faq.id;
            return (
              <div
                key={faq.id}
                className={`border-b transition-colors duration-300 ${
                  index === 0 ? 'border-t border-stone/20' : 'border-stone/20'
                }`}
              >
                <button
                  onClick={() => toggle(faq.id)}
                  className="w-full text-left py-6 flex items-start justify-between gap-6 group"
                  aria-expanded={isOpen}
                >
                  <span
                    className={`font-serif text-lg md:text-xl leading-snug transition-colors duration-300 ${
                      isOpen ? 'text-faded-navy' : 'text-stone group-hover:text-faded-navy'
                    }`}
                  >
                    {faq.question}
                  </span>
                  <span
                    className={`flex-shrink-0 mt-1 w-5 h-5 flex items-center justify-center transition-transform duration-300 ${
                      isOpen ? 'rotate-45' : ''
                    }`}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="text-stone/60"
                    >
                      <line x1="7" y1="0" x2="7" y2="14" stroke="currentColor" strokeWidth="1.2" />
                      <line x1="0" y1="7" x2="14" y2="7" stroke="currentColor" strokeWidth="1.2" />
                    </svg>
                  </span>
                </button>

                <div
                  className={`overflow-hidden transition-all duration-500 ease-in-out ${
                    isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <p className="font-sans text-stone text-base leading-relaxed pb-7 pr-8">
                    {faq.answer}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </main>
  );
}
